import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  getToken,
  setToken,
  removeToken,
  decodeToken,
  isTokenExpired,
} from "services/tokenUtils";
import { login as authLogin } from "services/authService";

export const UserContext = createContext({
  isLoggedIn: false,
  login: undefined,
  logout: undefined,
  userInfo: undefined,
});

UserContext.displayName = "UserContext";

export function UserProvider({ children }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    removeToken();
    setIsLoggedIn(false);
    setUserInfo(null);
    navigate("/sign-in");
  }, [navigate]);

  const redirectHandler = useCallback(
    (token) => {
      const { Role, name } = decodeToken(token) || {};
      if (!Role || !name) {
        logout();
        return;
      }
      navigate("/");
    },
    [logout, navigate]
  );

  const setTokenData = useCallback(
    (token) => {
      if (isTokenExpired(token)) {
        logout();
        return;
      }

      const { Role, name } = decodeToken(token) || {};
      if (!Role || !name) {
        logout();
        return;
      }

      setToken(token);
      setIsLoggedIn(true);
      setUserInfo({ Role, name });
    },
    [logout]
  );

  const login = useCallback(
    async (email, password) => {
      const response = await authLogin(email, password);
    
      if (!response || !response.token) {
        console.error("Login function received an invalid response:", response);
        throw new Error("Login failed. No token received.");
      }
  
      setTokenData(response.token);
      redirectHandler(response.token);
  
      return response; 
    },
    [redirectHandler, setTokenData]
  );

  useEffect(() => {
    const validateToken = async () => {
      const token = getToken();
      if (token) {
        await setTokenData(token);
      }
      setLoading(false);
    };
    const startTokenMonitor = () => {
      const intervalId = setInterval(() => {
        if (isTokenExpired()) {
          logout();
        }
      }, 1000 * 60);

      return () => clearInterval(intervalId);
    };

    validateToken();
    const intervalId = startTokenMonitor();
    return () => clearInterval(intervalId);
  }, [setTokenData, logout]);

  const value = useMemo(
    () => ({
      isLoggedIn,
      login,
      logout,
      userInfo,
      loading,
    }),
    [isLoggedIn, login, logout, userInfo, loading]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

UserProvider.propTypes = {
  children: PropTypes.node,
};

export const useUser = () => useContext(UserContext);
